// src/components/Team/userCard/UseTeamData.js
import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Axios from "../../../_services/caller.service";
import { getRoleLabel, isValidRole } from "../../../utils/constant/roles";

export function useTeamData({ ownerId, inviteId, userAuth }) {
  const { t } = useTranslation();
  const [isLoadingUsers, setIsLoadingUsers] = useState(true);
  const [isLoadingInvites, setIsLoadingInvites] = useState(true);

  // ROLE
  const [userRole, setUserRole] = useState([]);
  const [updateRole, setUpdateRole] = useState([]);
  const [majRole, setMajRole] = useState(false);

  // USERS + INVITES
  const [inviteUser, setInviteUser] = useState([]);
  const [inviteWaiting, setInviteWaiting] = useState([]);
  const [newInvite, setNewInvite] = useState(0);

  // reload flags
  const [maj, setMaj] = useState(false);
  const [reloadWaitingInvite, setReloadWaitingInvite] = useState("");

  // ===== fetch roles
  useEffect(() => {
    if (!ownerId) return;
    if (userAuth && Array.isArray(inviteId) && inviteId.length >= 1) {
      Axios.post(`/userRole/${ownerId}`, { data: inviteId })
        .then((res) => setUserRole(res.data.data || []))
        .catch(console.log);
    }
  }, [ownerId, inviteId, userAuth, majRole]);

  // ===== update role (si tu gardes ce flux)
  useEffect(() => {
    if (!ownerId) return;
    if (!updateRole || updateRole.length === 0) return;
    // ⚠️ ici tu avais updateUserId dans Team, si tu veux le garder il faut le remonter
    // sinon, fais l'updateRole directement depuis UserCard avec un userId en paramètre.
  }, [ownerId, updateRole, inviteId]);

  // ===== fetch users
  useEffect(() => {
    if (!ownerId) return;
    setIsLoadingUsers(true);
    Axios.get(`/getAuthCabineUser/${ownerId}`)
      .then((res) => setInviteUser(res.data.data || []))
      .catch(console.log)
      .finally(() => setIsLoadingUsers(false));
  }, [ownerId, maj]);

  // ===== fetch invites waiting
  useEffect(() => {
    if (!ownerId) return;
    setIsLoadingInvites(true);
    Axios.get(`/inviteWaiting/${ownerId}`)
      .then((res) => setInviteWaiting(res.data.data || []))
      .catch(console.log)
      .finally(() => setIsLoadingInvites(false));
  }, [ownerId, newInvite, reloadWaitingInvite]);

  // ===== helpers
  const displayRoleByUser = useCallback(
    (id) => {
      const role = userRole.filter((r) => String(id) === String(r.user)).map((c) => c.role);
      return t(getRoleLabel(role[0]));
    },
    [userRole, t]
  );

  const selectDefaultValue = useCallback(
    (id) => {
      const role = userRole.filter((r) => String(id) === String(r.user)).map((c) => c.role);
      return isValidRole(role[0]) ? Number(role[0]) : 0;
    },
    [userRole]
  );

  const numberCabsAuth = useCallback(
    (id) => {
      const userObj = inviteUser.find((u) => String(u.ID) === String(id));
      const list = userObj?.cabines?.[0] || [];
      return list.reduce((acc, cab) => acc + (cab?.auth ? 1 : 0), 0);
    },
    [inviteUser]
  );

  // ✅ NOUVEAU : all cab (avec userId param)
  const setAllCabPermissions = useCallback(
    async ({ userId, checked }) => {
      if (!ownerId || !userId) return;

      if (checked) {
        await Axios.post(`/createPermissionsAllCab/${ownerId}`, {
          data: { updateUserId: userId },
        });
      } else {
        await Axios.delete(`/deletePermissionsAllCab/${ownerId}?userId=${userId}`);
      }

      // refresh lists
      setMajRole((v) => !v);
      setMaj((v) => !v);
    },
    [ownerId]
  );

  // ✅ NOUVEAU : cab by cab (avec userId + cabId param)
  const setCabPermission = useCallback(
    async ({ userId, cabId, checked }) => {
      if (!ownerId || !userId || !cabId) return;

      if (checked) {
        await Axios.post(`/createPermissionsCabByCab/${ownerId}`, {
          updateUserId: userId,
          cabId,
          role: updateRole, // si besoin
        });
      } else {
        await Axios.delete(`/deletePermissionsCabByCab/${ownerId}?userId=${userId}&cabid=${cabId}`);
      }

      setMajRole((v) => !v);
      setMaj((v) => !v);
    },
    [ownerId, updateRole]
  );

  const invitesCount = Array.isArray(inviteWaiting) ? inviteWaiting.length : 0;
  const usersCount = Array.isArray(inviteUser) ? inviteUser.length : 0;

  const previewUsers = useMemo(() => {
    if (!Array.isArray(inviteUser)) return [];
    return inviteUser.slice(0, 3);
  }, [inviteUser]);

  return {
    // loading
    isLoadingUsers,
    isLoadingInvites,
    isLoading: isLoadingUsers || isLoadingInvites,

    // data
    inviteUser,
    inviteWaiting,
    userRole,

    // counts
    invitesCount,
    usersCount,
    previewUsers,

    // role
    updateRole,
    setUpdateRole,
    majRole,
    setMajRole,

    // reload
    maj,
    setMaj,
    reloadWaitingInvite,
    setReloadWaitingInvite,
    newInvite,
    setNewInvite,

    // setters (si tu en as besoin)
    setInviteUser,
    setInviteWaiting,
    setUserRole,

    // helpers
    displayRoleByUser,
    selectDefaultValue,
    numberCabsAuth,

    // ✅ actions fiables
    setAllCabPermissions,
    setCabPermission,
  };
}
